import React from "react";

import styled from "styled-components";

import {
    MessageContent,
    MessageInput,
    isMessageDocEmpty,
    legacyTextToMessageDoc,
    parseMessageDoc,
    stringifyMessageDoc,
} from "components/atoms/message";
import Text from "components/atoms/text/Text";

const Overlay = styled.div`
    position: fixed;
    z-index: 92;
    display: flex;
    flex-direction: column;
    width: min(400px, calc(100vw - 32px));
    max-height: min(440px, calc(100vh - 24px));
    padding: 12px;
    border: solid 1px var(--color-Line);
    border-radius: 8px;
    background-color: var(--color-White);
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
    overflow: hidden;
`;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
`;

const BodyScroll = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
`;

const HeaderTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const HeaderActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
`;

const SmallButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 10px;
    border: solid 1px ${({ $primary }) => ($primary ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 4px;
    background-color: ${({ $primary }) => ($primary ? "var(--color-Key)" : "var(--color-White)")};
    color: ${({ $primary }) => ($primary ? "var(--color-White)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        background-color: ${({ $primary }) => ($primary ? "var(--color-DarkKey)" : "var(--color-ButtonHover4)")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const CloseButton = styled.button`
    width: 28px;
    height: 28px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
    color: var(--color-SubBlack);
    font-size: 1.8rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const Quote = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})`
    display: block;
    overflow: hidden;
    padding: 8px;
    border-radius: 4px;
    background-color: var(--color-Base1);
    line-height: 1.4;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const MessageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Message = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px;
    border-radius: 4px;
    background-color: var(--color-Base1);
`;

const Meta = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const Composer = styled.div`
    flex: 0 0 auto;
`;

const AiRequestLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    user-select: none;

    input {
        margin: 0;
    }
`;

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getMessageSenderName(message) {
    if (message?.payload?.role === "ai") return "PREP";
    return message?.sender_name || "작성자 없음";
}

function normalizeMessageContent(content) {
    try {
        parseMessageDoc(content);
        return content || "";
    } catch (_error) {
        return stringifyMessageDoc(legacyTextToMessageDoc(content || ""));
    }
}

function clampPosition(position) {
    const overlayHeight = Math.min(440, window.innerHeight - 24);
    const top = Math.max(12, Math.min(window.innerHeight - overlayHeight - 12, Number(position?.top || 0)));
    const left = Math.max(12, Math.min(window.innerWidth - 416, Number(position?.left || 0)));
    return { top, left };
}

function NoteReviewThreadOverlay({
    mentions,
    onClose,
    onReopen,
    onReply,
    onResolve,
    overlayRef,
    position,
    replyRequestAI = false,
    replyDraft,
    replyLoading,
    setReplyRequestAI,
    setReplyDraft,
    thread,
}) {
    if (!thread || !position) return null;
    const safePosition = clampPosition(position);
    return (
        <Overlay
            ref={overlayRef}
            aria-label={`리뷰 #${thread.id}`}
            style={{ top: safePosition.top, left: safePosition.left }}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <Shell>
                <Header>
                    <HeaderTitle>
                        <Text variant="body-md-500" color="var(--color-SubBlack)">
                            리뷰 #{thread.id}
                        </Text>
                        <Meta>{thread.is_resolved ? "해결됨" : "열림"}</Meta>
                    </HeaderTitle>
                    <HeaderActions>
                        {thread.is_resolved ? (
                            <SmallButton type="button" onClick={() => onReopen?.(thread)}>
                                다시 열기
                            </SmallButton>
                        ) : (
                            <SmallButton type="button" onClick={() => onResolve?.(thread)}>
                                해결
                            </SmallButton>
                        )}
                        <CloseButton type="button" aria-label="닫기" onClick={onClose}>
                            ×
                        </CloseButton>
                    </HeaderActions>
                </Header>
                <Quote>{thread.selected_text || "선택 영역 정보가 없습니다."}</Quote>
                <BodyScroll>
                    <MessageList>
                        {(thread.messages || []).map((message) => (
                            <Message key={message.id}>
                                <Meta>
                                    {getMessageSenderName(message)} · {formatDateTime(message.created_at)}
                                </Meta>
                                <Text as="div" variant="body-sm-400" color="var(--color-SubBlack)">
                                    <MessageContent content={normalizeMessageContent(message.content)} />
                                </Text>
                            </Message>
                        ))}
                    </MessageList>
                </BodyScroll>
                {!thread.is_resolved ? (
                    <Composer>
                        <MessageInput
                            disabled={replyLoading}
                            mentions={mentions}
                            placeholder="답글을 입력하세요."
                            submitDisabled={replyLoading || (isMessageDocEmpty(replyDraft) && !replyRequestAI)}
                            submitLabel="답글"
                            submitLoading={replyLoading}
                            submitTooltip="답글"
                            value={replyDraft}
                            bottomSlot={
                                <AiRequestLabel>
                                    <input
                                        type="checkbox"
                                        checked={replyRequestAI}
                                        disabled={replyLoading}
                                        onChange={(event) => setReplyRequestAI?.(event.target.checked)}
                                    />
                                    AI 자동 응답
                                </AiRequestLabel>
                            }
                            onChange={setReplyDraft}
                            onSubmit={(nextValue) => onReply?.(thread, nextValue, { requestAI: replyRequestAI })}
                        />
                    </Composer>
                ) : null}
            </Shell>
        </Overlay>
    );
}

export default NoteReviewThreadOverlay;
